import type { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { MARKS_RE } from '../thai/segment';
import { isThaiText } from '../thai/ThaiTextbox';
import { isEdited } from '../editor/photo';
import { $ } from './dom';
import { kindName } from './objects';
import { toast } from './toast';

type Lockable = fabric.Object & { locked?: boolean; name?: string };

const ICON = {
  eye: '<svg viewBox="0 0 24 24"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>',
  eyeOff: '<svg viewBox="0 0 24 24"><path d="M3 3l18 18M10.6 6.1A10 10 0 0112 6c6 0 10 6 10 6a17 17 0 01-3.2 3.9M6.6 6.6C3.8 8.4 2 12 2 12s4 7 10 7a9.6 9.6 0 005-1.4"/></svg>',
  lock: '<svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/></svg>',
  unlock: '<svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 017.5-2"/></svg>',
  up: '<svg viewBox="0 0 24 24"><path d="M6 15l6-6 6 6"/></svg>',
  down: '<svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6"/></svg>'
};

export function labelOf(o: Lockable): string {
  if (isThaiText(o)) return (o.text ?? '').replace(MARKS_RE, '').replace(/\s+/g, ' ').trim().slice(0, 28) || 'ข้อความว่าง';
  return o.name ?? '';
}

export function setLocked(o: Lockable, on: boolean) {
  o.set({
    locked: on, selectable: !on, evented: !on, hasControls: !on,
    lockMovementX: on, lockMovementY: on, lockRotation: on, lockScalingX: on, lockScalingY: on
  } as Partial<fabric.Object>);
}

export function initLayersSheet(ed: Editor, history: History, onSelect: () => void) {
  const { canvas } = ed;
  let timer: ReturnType<typeof setTimeout> | undefined;

  function render() {
    const ul = $('layerList');
    ul.textContent = '';
    const objs = (ed.userObjects() as Lockable[]).slice().reverse();
    const act = canvas.getActiveObject();
    if (!objs.length) {
      const li = document.createElement('li');
      li.innerHTML = '<p class="empty">ยังไม่มีวัตถุบนหน้างาน</p>';
      ul.appendChild(li); return;
    }
    const changed = () => { canvas.requestRenderAll(); history.push(); render(); };

    objs.forEach((o, idx) => {
      const li = document.createElement('li');
      if (act && (act === o || (act.type === 'activeSelection' && (act as fabric.ActiveSelection).contains(o)))) li.className = 'sel';

      const name = document.createElement('button');
      name.className = 'lname';
      // a member of a group (จัดกลุ่ม) says so
      const tag = document.createElement('small'); tag.textContent = (kindName(o) ?? '') + ((o as { groupId?: string }).groupId ? ' · กลุ่ม' : '')
        + (o.type === 'image' && isEdited((o as { photoEdit?: unknown }).photoEdit) ? ' · แต่งภาพ' : '');
      name.append(tag, document.createTextNode(labelOf(o)));
      name.onclick = () => {
        if (o.locked) { toast('วัตถุนี้ล็อกอยู่ กดรูปกุญแจเพื่อปลดล็อกก่อน', true); return; }
        if (!o.visible) { toast('วัตถุนี้ถูกซ่อนอยู่ กดรูปตาเพื่อแสดงก่อน', true); return; }
        canvas.setActiveObject(o); canvas.requestRenderAll(); onSelect();
      };

      const btn = (icon: string, label: string, fn: () => void, pressed?: boolean) => {
        const b = document.createElement('button');
        b.className = 'lay'; b.innerHTML = icon; b.setAttribute('aria-label', label);
        if (pressed !== undefined) b.setAttribute('aria-pressed', String(pressed));
        b.onclick = fn; return b;
      };
      const eye = btn(o.visible ? ICON.eye : ICON.eyeOff, o.visible ? 'ซ่อน' : 'แสดง', () => {
        o.set('visible', !o.visible); if (!o.visible) canvas.discardActiveObject(); changed();
      });
      const lock = btn(o.locked ? ICON.lock : ICON.unlock, o.locked ? 'ปลดล็อก' : 'ล็อก', () => {
        setLocked(o, !o.locked); if (o.locked) canvas.discardActiveObject(); changed();
      }, !!o.locked);
      const up = btn(ICON.up, 'เลื่อนขึ้น', () => { canvas.bringForward(o); changed(); });
      const down = btn(ICON.down, 'เลื่อนลง', () => {
        canvas.sendBackwards(o);
        if (canvas.getObjects().indexOf(o) === 0) canvas.bringForward(o); // never below the artboard
        changed();
      });
      up.disabled = idx === 0; down.disabled = idx === objs.length - 1;
      li.append(name, eye, lock, up, down);
      ul.appendChild(li);
    });
  }

  return { render, refresh: () => { clearTimeout(timer); timer = setTimeout(render, 60); } };
}
